import { convertKeysToCamelCase } from '@/js/utils/bff'
import i18n from '@/lang/i18n'

/**
 * 點餐入口切換日
 * V2: 2026/04/14（含）前
 * V3: 2026/04/15 09:00 起（台灣時間 UTC+8）
 */
export const ORDER_FOOD_ENTRY_SWITCH_DATE = new Date('2026-04-15T09:00:00+08:00')
/**
 * 是否在切換日之前
 */
export const isBeforeOrderFoodEntrySwitchDate = () => {
  return new Date() < ORDER_FOOD_ENTRY_SWITCH_DATE
}

// 內用桌號列表儲存鍵
export const ORDER_FOOD_PRINT_TABLE_LIST = 'orderFoodPrintTableQrCodeTableList'

/**
 * 已取消訂單狀態 Enum
 * @enum {number}
 */
export const CanceledOrderState = Object.freeze({
  OrderRejected: 10, // 接單拒絕
  MealVoided: 12, // 餐點作廢
  PickupCanceled: 13, // 取餐取消
  UserCanceled: 14 // 住戶取消
})

/**
 * 判斷訂單 state 是否為取消類狀態
 * @param {number} state
 * @returns {boolean}
 */
export const isCanceledState = (state) => Object.values(CanceledOrderState).includes(state)

/**
 * 用餐方式 Enum
 * @enum {number}
 */
export const DiningOption = Object.freeze({
  DineIn: 1, // 內用
  Takeout: 2, // 現場外帶
  Reservation: 3 // 預約
})

/**
 * 取得顯示規格
 * @param {array} specList 規格列表
 * @param {string} separator 分隔符號
 * @param {function} filterFunc 過濾規格選項
 */
export const getDisplaySpec = (specList, separator = ', ', filterFunc = (opt) => opt.isOption === 1) => {
  let displaySpec = ''
  const selectedSpecOptions = specList.flatMap(spec => {
    return spec.children.filter(filterFunc)
  })
  if (selectedSpecOptions.length > 0) {
    displaySpec += selectedSpecOptions.map(opt => opt.name).join(separator)
  }
  return displaySpec
}

/**
 * 用餐方式選項文字
 * @param {number} diningOption 1 內用, 2 外帶, 3 預約
 * @returns {string}
 */
export const getDiningOptionLabel = (diningOption) => {
  if (diningOption === DiningOption.DineIn) return i18n.t('OrderFoodManagement.DiningOptionDineIn')
  if (diningOption === DiningOption.Takeout) return i18n.t('OrderFoodManagement.DiningOptionTakeout')
  if (diningOption === DiningOption.Reservation) return i18n.t('OrderFoodManagement.DiningOptionReservation')
  return '-'
}

/**
 * 用餐方式資訊文字
 * @param {Object} dining 用餐資料
 * @param {number} dining.diningOption 1 內用, 2 現場外帶, 3 預約
 * @param {string} dining.tableNo 桌號
 * @returns {string}
 */
export const getDiningInfoText = (dining) => {
  if (!dining) return '-'
  const { diningOption, tableNo } = dining
  if (diningOption === DiningOption.DineIn) return tableNo || '-'
  if (diningOption === DiningOption.Takeout) return i18n.t('OrderFoodManagement.DiningInfoTakeout')
  if (diningOption === DiningOption.Reservation) return i18n.t('OrderFoodManagement.DiningInfoTakeout')
  return '-'
}

/**
 * 用餐方式合併字串（給列印或你要的單一欄位顯示）
 * 內用 => 內用 / 桌號
 * 外帶 => 外帶
 * 預約 => 預約自取 YYYY/MM/DD HH:ss
 * @param {Object} dining 用餐資料
 * @param {number} dining.diningOption 1 內用, 2 現場外帶, 3 預約
 * @param {string} dining.tableNo 桌號
 * @param {string} dining.reservationAtUtc 預約時間
 * @returns {string}
 */
export const getDiningDisplayText = (dining) => {
  if (!dining) return '-'
  const { diningOption, tableNo } = dining
  const optionLabel = getDiningOptionLabel(diningOption)
  if (diningOption === DiningOption.DineIn) {
    return `${optionLabel} / ${tableNo || '-'}`
  } else if (diningOption === DiningOption.Takeout) {
    return `${optionLabel}`
  } else if (diningOption === DiningOption.Reservation) {
    const timeStr = dining.reservationAtUtc
      ? `${dayjs(dining.reservationAtUtc).format('YYYY-MM-DD HH:mm')}`
      : ''
    return `${optionLabel}${i18n.t('OrderFoodManagement.DiningInfoTakeout')}${timeStr ? ` / ${timeStr}` : ''}`
  }
  return optionLabel
}

/**
 * 取得社區戶別點數資訊
 * @param {string} tabletId 戶別ID
 * @returns {Promise<{status: number, data: object}>} 回傳包含 status 和 data 的物件
 */
export const getCommunityTabletPointsData = async (tabletId) => {
  // lazy import: 避免與 router / createAxiosInstance 形成循環依賴
  const { apiOrderFood } = await import('@/api/OrderFood')
  const response = await apiOrderFood.getCommunityTabletPoints(tabletId)
  const { status, data } = convertKeysToCamelCase(response.data)
  return { status, data }
}

/**
 * 取值的最後四碼
 * @param {string|number|null|undefined} value 需要顯示的ID
 * @param {string} placeholder 當值不存在時顯示
 * @returns {string}
 */
export const getLast4Digits = (value, placeholder = '-') => {
  if (value === undefined || value === null) return placeholder
  const str = String(value).trim()
  if (!str) return placeholder
  const digitsOnly = str.replace(/\D/g, '')
  if (!digitsOnly) return placeholder
  if (digitsOnly.length <= 4) return digitsOnly
  return digitsOnly.slice(-4)
}

/**
 * 格式化訂單顯示代碼：月日（MMDD）＋訂單編號尾部四碼
 * @param {string|number|null|undefined} comOrderId 訂單編號
 * @param {string|Date|null|undefined} createdAtUtc 訂單建立時間（UTC）
 * @param {string} placeholder 當值不存在時顯示
 * @returns {string} 例如 "04161234"
 */
export const formatOrderDisplayCode = (comOrderId, createdAtUtc, placeholder = '-') => {
  const last4 = getLast4Digits(comOrderId, null)
  if (!last4) return placeholder
  if (!createdAtUtc) return last4
  const d = new Date(createdAtUtc)
  if (isNaN(d.getTime())) return last4
  const pad = (n) => String(n).padStart(2, '0')
  const mmdd = `${pad(d.getMonth() + 1)}${pad(d.getDate())}`
  return `${mmdd}${last4}`
}
